/**
 * Pricing and entitlement rules for CallSync plans.
 *
 * Imported by the browser (to show a quote) and by the Pages Functions (to
 * charge and grant). The server always recomputes the quote from the live
 * organisation row — a total sent by the browser is never trusted.
 */

export type Currency = 'INR' | 'USD';

export const UNIT_PRICES: Record<Currency, { user: number; storageGb: number; webhookPerUser: number }> = {
  INR: { user: 149, storageGb: 99, webhookPerUser: 99 },
  USD: { user: 5.99, storageGb: 1.99, webhookPerUser: 2 },
};

export const TERMS = [
  { months: 1, discountPercent: 0, label: '1 month' },
  { months: 3, discountPercent: 20, label: '3 months' },
] as const;

/**
 * New trial workspaces. Storage is 200 MB (200/1024 GB, the value the
 * database's trial policy enforces); paid storage is sold in whole GB, so an
 * upgrade from a trial starts at `upgradeStorageGb` instead.
 */
export const TRIAL = {
  days: 7,
  users: 2,
  storageGb: 200 / 1024,
  upgradeStorageGb: 2,
  aiCredits: 50,
  integrations: true,
} as const;

export const LIMITS = { maxUsers: 500, maxStorageGb: 5000 } as const;

/** Storage steppers move in 2 GB steps, like the website calculator. */
export const STORAGE_STEP_GB = 2;

/** Fixed AI rates shared by Razorpay and Paddle checkout. */
export const AI_CREDITS = {
  inrPerCredit: 0.25,
  // Fixed USD rate set for AI credits.
  usdPerCredit: 0.005,
  secondsPerCredit: 15,
  packSize: 4000,
  maxPurchase: 100_000,
} as const;

export interface PlanState {
  planId: string | null;
  planExpiry: string | null;
  allowedUsers: number;
  allowedStorageGb: number;
  hasWebhookAccess: boolean;
  renewalPlan?: PlanCapacity | null;
  scheduledPlan?: ScheduledPlan | null;
}

export interface PlanCapacity {
  users: number;
  storageGb: number;
  webhooks: boolean;
}

export interface ScheduledPlan extends PlanCapacity {
  startsAt: string;
  expiresAt: string;
}

/** Storage for display: under 1 GB reads in MB (the trial's 200 MB). */
export function formatStorageGb(gb: number): string {
  return gb > 0 && gb < 1 ? `${Math.round(gb * 1024)} MB` : `${gb} GB`;
}

/**
 * Capacity a plan form starts from. A trial's 200 MB would floor to 0 GB in a
 * quote, so an upgrade from a trial starts at the paid default instead; the
 * trial allowance is replaced, never added to. Trials include integrations for
 * free, so a first plan starts without that paid add-on until the owner turns it on.
 */
export function startingCapacity(plan: PlanState, now = new Date()): PlanCapacity {
  const paid = getPlanStatus(plan, now).isPaid;
  return {
    users: Math.max(1, plan.allowedUsers),
    storageGb: paid ? plan.allowedStorageGb : Math.max(TRIAL.upgradeStorageGb, Math.ceil(plan.allowedStorageGb)),
    webhooks: paid && plan.hasWebhookAccess,
  };
}

/** Project a prepaid renewal only once its current paid term has ended. */
export function planAt(plan: PlanState, now = new Date()): PlanState {
  const next = plan.scheduledPlan;
  if (!next || !Number.isFinite(Date.parse(next.startsAt)) || Date.parse(next.startsAt) > now.getTime())
    return plan;
  return {
    ...plan,
    planId: 'paid',
    planExpiry: next.expiresAt,
    allowedUsers: next.users,
    allowedStorageGb: next.storageGb,
    hasWebhookAccess: next.webhooks,
    renewalPlan: null,
    scheduledPlan: null,
  };
}

export function validateReduction(plan: PlanState, value: PlanCapacity, now = new Date()): string | null {
  if (!getPlanStatus(plan, now).canAddToTerm)
    return 'An active paid plan is needed to reduce next renewal’s capacity.';
  if (plan.scheduledPlan) return 'Your next term is already paid. Changes can be made after it starts.';
  if (
    !Number.isInteger(value.users) ||
    value.users < 1 ||
    value.users > plan.allowedUsers ||
    !Number.isInteger(value.storageGb) ||
    value.storageGb < 0 ||
    value.storageGb > plan.allowedStorageGb ||
    typeof value.webhooks !== 'boolean' ||
    (value.webhooks && !plan.hasWebhookAccess)
  )
    return 'Choose capacity at or below your current plan.';
  if (
    value.users === plan.allowedUsers &&
    value.storageGb === plan.allowedStorageGb &&
    value.webhooks === plan.hasWebhookAccess
  )
    return 'Reduce phones, storage or integrations to save a smaller renewal plan.';
  return null;
}

/** Shared by the server and demo: an early reduction must never remove paid capacity. */
export function applyPlanEntitlements(
  input: PlanState,
  entitlements: PlanEntitlements,
  now = new Date(),
): PlanState {
  const plan = planAt(input, now);
  if (!entitlements.is_renewing)
    return {
      ...plan,
      allowedUsers: entitlements.user_count,
      allowedStorageGb: entitlements.storage_gb,
      hasWebhookAccess: entitlements.has_webhook_access,
    };
  const expiresAt = computeNewExpiry(plan, entitlements.duration_months, now).toISOString();
  const reduces =
    entitlements.user_count < plan.allowedUsers ||
    entitlements.storage_gb < plan.allowedStorageGb ||
    (plan.hasWebhookAccess && !entitlements.has_webhook_access);
  if (getPlanStatus(plan, now).canAddToTerm && reduces && plan.planExpiry) {
    return {
      ...plan,
      allowedUsers: Math.max(plan.allowedUsers, entitlements.user_count),
      allowedStorageGb: Math.max(plan.allowedStorageGb, entitlements.storage_gb),
      hasWebhookAccess: plan.hasWebhookAccess || entitlements.has_webhook_access,
      renewalPlan: null,
      scheduledPlan: {
        users: entitlements.user_count,
        storageGb: entitlements.storage_gb,
        webhooks: entitlements.has_webhook_access,
        startsAt: plan.planExpiry,
        expiresAt,
      },
    };
  }
  return {
    ...plan,
    planId: 'paid',
    planExpiry: expiresAt,
    allowedUsers: entitlements.user_count,
    allowedStorageGb: entitlements.storage_gb,
    hasWebhookAccess: entitlements.has_webhook_access,
    renewalPlan: null,
    scheduledPlan: null,
  };
}

export interface PlanStatus {
  isPaid: boolean;
  isTrial: boolean;
  isExpired: boolean;
  /** Whole days until expiry; 0 once expired. */
  daysLeft: number;
  /** Resources can be added to the running term only on an active paid plan. */
  canAddToTerm: boolean;
}

const DAY_MS = 86_400_000;

export function getPlanStatus(plan: Pick<PlanState, 'planId' | 'planExpiry'>, now = new Date()): PlanStatus {
  const expiry = plan.planExpiry ? new Date(plan.planExpiry) : null;
  const msLeft = expiry && !Number.isNaN(expiry.getTime()) ? expiry.getTime() - now.getTime() : -1;
  const isExpired = msLeft <= 0;
  const isPaid = plan.planId === 'paid';
  return {
    isPaid,
    isTrial: !isPaid,
    isExpired,
    daysLeft: isExpired ? 0 : Math.ceil(msLeft / DAY_MS),
    canAddToTerm: isPaid && !isExpired,
  };
}

/** Trial day from which the dashboard asks the owner to pay (day 1 is signup day). */
export const PAY_NOW_FROM_TRIAL_DAY = 5;

/**
 * Whether to show the "pay now" banner: an active trial on day 5 or later, judged by the days
 * left, since a trial's expiry is set [TRIAL.days] after signup.
 */
export function showPayNowBanner(status: PlanStatus): boolean {
  return status.isTrial && !status.isExpired && status.daysLeft <= TRIAL.days - PAY_NOW_FROM_TRIAL_DAY + 1;
}

/** "₹149" or "$5.99": the price of one phone for one month. */
export function seatPriceLabel(currency: Currency): string {
  return currency === 'USD' ? `$${UNIT_PRICES.USD.user}` : `₹${UNIT_PRICES.INR.user}`;
}

/**
 * Where a renewal's new term starts: at the current expiry for an active paid
 * plan, otherwise today (a trial's remaining days are not carried over).
 */
export function computeNewExpiry(
  plan: Pick<PlanState, 'planId' | 'planExpiry'>,
  months: number,
  now = new Date(),
): Date {
  const status = getPlanStatus(plan, now);
  const base = status.canAddToTerm && plan.planExpiry ? new Date(plan.planExpiry) : new Date(now);
  base.setMonth(base.getMonth() + months);
  return base;
}

export type QuoteMode = 'renew' | 'add';

export interface QuoteRequest {
  mode: QuoteMode;
  /** renew: seats for the new term. add: seats to add to the running term. */
  users: number;
  /** renew: GB for the new term. add: GB to add to the running term. */
  storageGb: number;
  /** renew: include webhooks in the new term. add: switch webhooks on now. */
  webhooks: boolean;
  /** Only used for renew. */
  termMonths: number;
  promoPercent?: number;
  currency?: Currency;
}

export interface QuoteLine {
  label: string;
  detail: string;
  amount: number;
}

/** Shape stored in razorpay_payments.checkout_data and applied after payment. */
export interface PlanEntitlements {
  user_count: number;
  storage_gb: number;
  has_webhook_access: boolean;
  duration_months: number;
  is_renewing: boolean;
}

export interface Quote {
  ok: boolean;
  error?: string;
  currency: Currency;
  lines: QuoteLine[];
  subtotal: number;
  termDiscountPercent: number;
  termDiscount: number;
  promoPercent: number;
  promoDiscount: number;
  total: number;
  entitlements: PlanEntitlements;
  /** ISO date the plan will run until after this purchase. */
  expiresAt: string | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

const toInt = (n: unknown, min: number, max: number) => {
  const v = Math.floor(Number(n));
  return Math.min(max, Math.max(min, Number.isFinite(v) ? v : 0));
};

export function formatMoney(amount: number, currency: Currency): string {
  return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

export function computeQuote(plan: PlanState, req: QuoteRequest, now = new Date()): Quote {
  plan = planAt(plan, now);
  const currency: Currency = req.currency === 'USD' ? 'USD' : 'INR';
  const price = UNIT_PRICES[currency];
  const status = getPlanStatus(plan, now);
  const money = (n: number) => formatMoney(n, currency);
  const lines: QuoteLine[] = [];
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  const base: Omit<Quote, 'ok' | 'lines' | 'subtotal' | 'termDiscount' | 'promoDiscount' | 'total'> = {
    currency,
    termDiscountPercent: 0,
    promoPercent: 0,
    entitlements: {
      user_count: plan.allowedUsers,
      storage_gb: plan.allowedStorageGb,
      has_webhook_access: plan.hasWebhookAccess,
      duration_months: 0,
      is_renewing: false,
    },
    expiresAt: plan.planExpiry,
  };
  const fail = (error: string): Quote => ({
    ...base,
    ok: false,
    error,
    lines: [],
    subtotal: 0,
    termDiscount: 0,
    promoDiscount: 0,
    total: 0,
  });

  if (req.mode === 'add') {
    if (!status.canAddToTerm) return fail('Start or renew a plan before adding seats or storage.');

    const addUsers = toInt(req.users, 0, Math.max(0, LIMITS.maxUsers - plan.allowedUsers));
    const addStorage = toInt(req.storageGb, 0, Math.max(0, LIMITS.maxStorageGb - plan.allowedStorageGb));
    const addWebhooks = Boolean(req.webhooks) && !plan.hasWebhookAccess;
    if (!addUsers && !addStorage && !addWebhooks)
      return fail('Add at least one seat, some storage or webhooks.');

    const factor = status.daysLeft / 30;
    const period = `prorated for the ${plural(status.daysLeft, 'day')} left in this term`;
    const totalUsers = plan.allowedUsers + addUsers;

    if (addUsers) {
      lines.push({
        label: `${plural(addUsers, 'extra seat')}`,
        detail: `${money(price.user)} per seat a month, ${period}`,
        amount: round2(addUsers * price.user * factor),
      });
    }
    if (addStorage) {
      lines.push({
        label: `${addStorage} GB extra storage`,
        detail: `${money(price.storageGb)} per GB a month, ${period}`,
        amount: round2(addStorage * price.storageGb * factor),
      });
    }
    if (addWebhooks) {
      lines.push({
        label: 'Webhooks & API',
        detail: `${money(price.webhookPerUser)} per seat a month for ${plural(totalUsers, 'seat')}, ${period}`,
        amount: round2(totalUsers * price.webhookPerUser * factor),
      });
    } else if (plan.hasWebhookAccess && addUsers) {
      lines.push({
        label: 'Webhooks for the new seats',
        detail: `${money(price.webhookPerUser)} per seat a month, ${period}`,
        amount: round2(addUsers * price.webhookPerUser * factor),
      });
    }

    base.entitlements = {
      user_count: totalUsers,
      storage_gb: plan.allowedStorageGb + addStorage,
      has_webhook_access: plan.hasWebhookAccess || addWebhooks,
      duration_months: 0,
      is_renewing: false,
    };
  } else {
    if (plan.scheduledPlan)
      return fail('Your next term is already paid. You can renew again after it starts.');
    const term = TERMS.find((t) => t.months === Number(req.termMonths));
    if (!term) return fail('Choose a 1 or 3 month term.');

    const users = toInt(req.users, 1, LIMITS.maxUsers);
    const storage = toInt(req.storageGb, 0, LIMITS.maxStorageGb);
    const months = term.months;
    const webhooks = Boolean(req.webhooks);
    const termLabel = plural(months, 'month');

    lines.push({
      label: plural(users, 'seat'),
      detail: `${money(price.user)} per seat a month × ${termLabel}`,
      amount: round2(users * price.user * months),
    });
    if (storage > 0) {
      lines.push({
        label: `${storage} GB recording storage`,
        detail: `${money(price.storageGb)} per GB a month × ${termLabel}`,
        amount: round2(storage * price.storageGb * months),
      });
    }
    if (webhooks) {
      lines.push({
        label: 'Webhooks & API',
        detail: `${money(price.webhookPerUser)} per seat a month × ${plural(users, 'seat')} × ${termLabel}`,
        amount: round2(users * price.webhookPerUser * months),
      });
    }

    // Growing a running paid term: the increase also applies from today until
    // the new term begins at the current expiry.
    if (status.canAddToTerm) {
      const extraUsers = Math.max(0, users - plan.allowedUsers);
      const extraStorage = Math.max(0, storage - plan.allowedStorageGb);
      const newWebhooks = webhooks && !plan.hasWebhookAccess;
      const extraMonthly =
        extraUsers * price.user +
        extraStorage * price.storageGb +
        (newWebhooks ? users * price.webhookPerUser : webhooks ? extraUsers * price.webhookPerUser : 0);
      if (extraMonthly > 0) {
        lines.push({
          label: 'Upgrade until your current term ends',
          detail: `The increase, prorated for ${plural(status.daysLeft, 'day')}`,
          amount: round2(extraMonthly * (status.daysLeft / 30)),
        });
      }
    }

    base.termDiscountPercent = term.discountPercent;
    base.entitlements = {
      user_count: users,
      storage_gb: storage,
      has_webhook_access: webhooks,
      duration_months: months,
      is_renewing: true,
    };
    base.expiresAt = computeNewExpiry(plan, months, now).toISOString();
  }

  const subtotal = round2(lines.reduce((sum, l) => sum + l.amount, 0));
  const termDiscount = round2((subtotal * base.termDiscountPercent) / 100);
  const promoPercent = Math.min(100, Math.max(0, Number(req.promoPercent) || 0));
  const promoDiscount = round2(((subtotal - termDiscount) * promoPercent) / 100);
  const total = Math.max(0, round2(subtotal - termDiscount - promoDiscount));

  return { ...base, ok: true, lines, subtotal, termDiscount, promoPercent, promoDiscount, total };
}

export function computeCreditPurchase(credits: unknown, currency: Currency = 'INR') {
  const count = toInt(credits, 0, AI_CREDITS.maxPurchase);
  return {
    credits: count,
    amount: round2(count * (currency === 'USD' ? AI_CREDITS.usdPerCredit : AI_CREDITS.inrPerCredit)),
  };
}
